import { Injectable } from '@angular/core';

import { compareInverterFWModuleVersions, DeviceUpgradeConfig } from 'ngx-modbus';

import { FirmwareConfigService } from '../lib/firmware-config/firmware-config.service';

const isSerialMatch = (key: string, serial: string): boolean => (new RegExp(key, 'i')).test(serial.trim());

@Injectable({
  providedIn: 'root',
})
export class AccessSupportService {

  constructor(
    private readonly firmwareConfigService: FirmwareConfigService
  ) {}

  isDashboardSupported = async (serial: string): Promise<boolean> =>
    Object.entries(await this.firmwareConfigService.getFirmwareConfig())
      .filter(([_, value]) => value.DEVICE_MODEL_ID === 'INVERTER' && value.MODE.includes('DASHBOARD'))
      .some(([key, _]) => isSerialMatch(key, serial));

  isSupported = async (serial: string): Promise<boolean> =>
    Object.entries(await this.firmwareConfigService.getFirmwareConfig())
      .some(([key, _]) => isSerialMatch(key, serial));

  isOverMinVersion = async (serial: string, version: string): Promise<boolean> => {
    const deviceUpgradeConfig = await this.getDeviceModuleInfo(serial);
    if (!deviceUpgradeConfig) {
      return false;
    }

    if (deviceUpgradeConfig.DEVICE_MODEL_ID !== 'INVERTER') {
      return false;
    }

    const minVersions = deviceUpgradeConfig.ARM?.map((chipData) => chipData.MIN_VERSION);
    if (minVersions?.length === 0) {
      return false;
    }

    return !!minVersions?.some((minVersion) => (compareInverterFWModuleVersions(version, minVersion) || 0) >= 0);
  };

  getDeviceModuleInfo = async (serial: string): Promise<DeviceUpgradeConfig | null> => {
    const config = await this.firmwareConfigService.getFirmwareConfig();
    const deviceInfo = Object.entries(config).find(([key, _value]) =>
      isSerialMatch(key, serial),
    );

    return deviceInfo ? deviceInfo[1] : null;
  };

  supportedSerials = async () => {
    const serials = Object.values(await this.firmwareConfigService.getFirmwareConfig())
      .map((value) => value.ID);

    const uniqueSerials = [...new Set(serials)];
    return [...uniqueSerials].join(', ');
  };
}
